import { useRef, useState, useEffect } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
  useMotionValue,
  useAnimationFrame,
} from "motion/react";
import { OrbitalRings, RING_2_RADIUS } from "../ui/OrbitalRings";
import { SparklingStar } from "../ui/SparklingStar";
import { ImageWithFallback } from "../ImageWithFallback";
import mascot from "../../assets/geekcow-mascot.png";
import { BRAND } from "../../lib/brand";
import { HERO, HERO_VERBS, HERO_NOUNS, CERTIFICATIONS, USP_TEXT } from "../../data/agencyData";

export function AgencyHero() {
  const sectionRef = useRef<HTMLElement>(null);
  const [mouse, setMouse] = useState({ x: 0, y: 0 });
  const [verbIndex, setVerbIndex] = useState(0);
  const [nounIndex, setNounIndex] = useState(0);

  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ["start start", "end start"] });
  const ringRotations = [
    useTransform(scrollYProgress, [0, 1], [0, 360]),
    useTransform(scrollYProgress, [0, 1], [0, -270]),
    useTransform(scrollYProgress, [0, 1], [0, 180]),
    useTransform(scrollYProgress, [0, 1], [0, -90]),
  ];
  const mascotY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  // Continuous drift, same technique as the illustration in the contact section:
  // a slow angle driving an ellipse, plus a gentle tilt so it never sits still.
  const driftAngle = useMotionValue(0);
  useAnimationFrame((t) => driftAngle.set(t * 0.00022)); // ~28s per lap
  // Biased vertical: the long verbs (IMPROVE, SUPPORT) reach across to the
  // mascot, so a wide horizontal swing would make that overlap visibly pulse.
  const driftX = useTransform(driftAngle, (a) => Math.cos(a) * 12);
  const driftY = useTransform(driftAngle, (a) => Math.sin(a) * 38);
  const driftRotate = useTransform(driftAngle, (a) => Math.sin(a * 0.7) * 5);

  useEffect(() => {
    // Intentionally not multiples of each other: the two words drift in and out
    // of phase, so you get varied pairings instead of a fixed repeating couplet.
    const verbTimer = setInterval(() => setVerbIndex((i) => (i + 1) % HERO_VERBS.length), 2400);
    const nounTimer = setInterval(() => setNounIndex((i) => (i + 1) % HERO_NOUNS.length), 3300);
    return () => {
      clearInterval(verbTimer);
      clearInterval(nounTimer);
    };
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMouse({ x: e.clientX / window.innerWidth - 0.5, y: e.clientY / window.innerHeight - 0.5 });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  return (
    <section
      ref={sectionRef}
      // gc-ring-field defines --gc-ring-d for the whole section, so the mascot
      // can be anchored to a ring radius even though it sits outside <OrbitalRings/>.
      className="gc-ring-field relative w-full min-h-[100svh] flex items-center justify-center overflow-hidden px-4 pt-[120px] pb-16"
      style={{ perspective: "900px" }}
    >
      <OrbitalRings mouse={mouse} scrollRotations={ringRotations} variant="flat" />

      <SparklingStar className="left-[74%] top-[24%] w-[32px] h-[25px]" duration={3}>
        <svg className="block size-full" fill="none" viewBox="0 0 20 20">
          <path d="M10 0L11.25 8.75L20 10L11.25 11.25L10 20L8.75 11.25L0 10L8.75 8.75L10 0Z" fill={BRAND.ink} />
        </svg>
      </SparklingStar>
      <SparklingStar className="left-[34%] top-[62%] w-[20px] h-[20px]" duration={4} delay={0.6}>
        <svg className="block size-full" fill="none" viewBox="0 0 20 20">
          <path d="M10 0L11.25 8.75L20 10L11.25 11.25L10 20L8.75 11.25L0 10L8.75 8.75L10 0Z" fill={BRAND.ink} />
        </svg>
      </SparklingStar>
      <SparklingStar className="left-[66%] top-[46%] w-[18px] h-[18px]" duration={3.4} delay={1.1}>
        <svg className="block size-full" fill="none" viewBox="0 0 20 20">
          <path d="M10 0L11.25 8.75L20 10L11.25 11.25L10 20L8.75 11.25L0 10L8.75 8.75L10 0Z" fill={BRAND.ink} />
        </svg>
      </SparklingStar>

      {/* Mascot, offset left as in the design */}
      {/* In the design the mascot is not floating — its centre sits exactly on the
          second dashed ring, left of the headline. Anchoring it to that radius
          keeps it on the line at every viewport size.
          The static offset lives on this wrapper, not on the motion element —
          motion writes its own inline `transform`, which would drop the
          -translate classes and leave the mascot misplaced. */}
      <div
        className="pointer-events-none absolute top-1/2 hidden md:block w-[210px] lg:w-[270px] xl:w-[320px] -translate-x-1/2 -translate-y-1/2"
        style={{ left: `calc(50% - ${RING_2_RADIUS})` }}
      >
        {/* Each behaviour gets its own layer: they all write `transform`, so
            stacking them is what keeps parallax, drift and drag from
            overwriting one another. */}
        <motion.div style={{ y: mascotY, x: mouse.x * -24 }} aria-hidden="true">
          <motion.div style={{ x: driftX, y: driftY, rotate: driftRotate }}>
            <motion.div
              drag
              dragConstraints={sectionRef}
              dragMomentum={false}
              dragElastic={0.08}
              whileDrag={{ scale: 1.06, cursor: "grabbing" }}
              whileHover={{ scale: 1.06 }}
              transition={{ type: "spring", stiffness: 260, damping: 18 }}
              className="pointer-events-auto"
              style={{ cursor: "grab", touchAction: "none" }}
              title="Drag me!"
            >
              <ImageWithFallback
                src={mascot}
                alt=""
                className="w-full h-auto drop-shadow-2xl select-none pointer-events-none"
              />
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* w-full is load-bearing: the rotating words are absolutely positioned, so
          they add nothing to this column's intrinsic width. Without it the column
          shrinks to the other content (~560px) and overflow-hidden on each word
          slot clips the 7-letter words (WEBSITE, IMPROVE, SUPPORT, PROJECT). */}
      <div className="relative z-10 w-full flex flex-col items-center gap-6 text-center max-w-[1000px]">
        {/* Mixed case and a bolder "GC", exactly as drawn in the design */}
        <p className="font-mono text-[11px] md:text-[14px] tracking-[3px] md:tracking-[4px] text-[#003e5b]">
          WE ARE GEEKCOW ( aka <strong className="font-bold text-[#141414]">GC</strong> ) CREATIVE STUDIO
        </p>

        <h1 className="w-full flex flex-col items-center leading-[0.88] tracking-tighter text-[#141414]">
          <span className="font-black text-[56px] md:text-[96px]">We</span>

          {/* Rotating verb: CREATE → BUILD → THINK → … */}
          <span className="relative block h-[68px] md:h-[124px] w-full overflow-hidden">
            <span className="sr-only">create, build, think, improve, support, secure or design</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={HERO_VERBS[verbIndex]}
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center font-black text-[62px] md:text-[116px] whitespace-nowrap"
                style={{ textShadow: `-4px 3px 0 ${BRAND.red}, 4px -3px 0 ${BRAND.blue}` }}
                initial={{ opacity: 0, y: 34, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -34, filter: "blur(8px)" }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              >
                {HERO_VERBS[verbIndex]}
              </motion.span>
            </AnimatePresence>
          </span>

          <span className="font-black text-[46px] md:text-[84px]">YOUR</span>

          {/* Rotating noun: IDEA → PROJECT → WEBSITE → APP */}
          <span className="relative block h-[68px] md:h-[124px] w-full overflow-hidden">
            <span className="sr-only">idea, project, website or app</span>
            <AnimatePresence mode="wait">
              <motion.span
                key={HERO_NOUNS[nounIndex]}
                aria-hidden="true"
                className="absolute inset-0 flex items-center justify-center font-black text-[62px] md:text-[116px] whitespace-nowrap"
                style={{ textShadow: `-4px 3px 0 ${BRAND.red}, 4px -3px 0 ${BRAND.blue}` }}
                initial={{ opacity: 0, y: 34, filter: "blur(8px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -34, filter: "blur(8px)" }}
                transition={{ duration: 0.45, ease: "easeOut" }}
              >
                {HERO_NOUNS[nounIndex]}
              </motion.span>
            </AnimatePresence>
          </span>
        </h1>

        <p className="text-[16px] md:text-[20px] text-[#003e5b] max-w-[560px] leading-[1.5]">{HERO.subline}</p>

        {/* The differentiator, moved up from Our Process: it is the reason to
            choose the studio, so it belongs in the hero rather than buried.
            Set in the same soft grey as the certification labels below, so it
            reads as a supporting line rather than a second headline. */}
        <p className="text-[15px] md:text-[18px] text-[#5e5e5e] max-w-[620px] leading-[1.5]">
          {USP_TEXT}
        </p>

        {/* Certifications sit here rather than down in Our Stack: they are proof
            of the claim in the line above, and they belong beside it. */}
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 mt-1">
          {CERTIFICATIONS.map((cert) => (
            <a
              key={cert.name}
              href={cert.href}
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-2 opacity-70 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003e5b] rounded-lg p-1"
            >
              {cert.name === "Microsoft" ? (
                <svg className="w-6 h-6 md:w-7 md:h-7 text-[#F25022]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M11.5 11.5H0V0h11.5v11.5zm12.5 0H12.5V0H24v11.5zM11.5 24H0V12.5h11.5V24zm12.5 0H12.5V12.5H24V24z" />
                </svg>
              ) : (
                <svg className="w-6 h-6 md:w-7 md:h-7" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              )}
              <span className="flex flex-col text-left leading-tight">
                <span className="font-bold text-[13px] md:text-[15px] text-[#5e5e5e] group-hover:text-[#141414]">
                  {cert.name}
                </span>
                <span className="text-[10px] md:text-[11px] text-[#666]">{cert.detail}</span>
              </span>
            </a>
          ))}
        </div>

        {/* Primary call to action — the hero previously had none. */}
        <a
          href="#contact"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("contact")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="group mt-2 inline-flex items-center gap-3 rounded-full border-2 border-[#141414] px-7 py-4 font-bold text-[13px] tracking-[2px] uppercase text-[#003e5b] shadow-[0_6px_0_-1px_rgba(0,0,0,0.18)] transition-all hover:-translate-y-0.5 hover:shadow-[0_9px_0_-1px_rgba(0,0,0,0.22)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#003e5b]/30"
          style={{ background: BRAND.greenBright }}
        >
          Start your project
          <span className="transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true">
            →
          </span>
        </a>

        <a
          href="#about"
          onClick={(e) => {
            e.preventDefault();
            document.getElementById("about")?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className="mt-2 p-2 rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#003e5b]"
          aria-label="Scroll to how we work"
        >
          <motion.svg
            className="w-[18px] h-[20px]"
            viewBox="0 0 14 20"
            fill="none"
            animate={{ y: [0, 7, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
            aria-hidden="true"
          >
            <path d="M7 0V19" stroke={BRAND.navy} strokeWidth="2" strokeLinecap="round" />
            <path d="M13 13L7 19L1 13" stroke={BRAND.navy} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </motion.svg>
        </a>
      </div>
    </section>
  );
}
